"use client";

import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { useState } from "react";
import ScrollReveal from "@/app/components/ScrollReveal";

type LogoSize = "sm" | "lg" | "xl";

const pressLogos: { src: string; alt: string; size: LogoSize }[] = [
  { src: "/images/uproxx-logo.png", alt: "UPROXX", size: "sm" },
  { src: "/images/Time-Out-logo_edited.png", alt: "TimeOut", size: "sm" },
  { src: "/images/The-new-yorker-logo.jpg", alt: "The New Yorker", size: "sm" },
  { src: "/images/ny-times-logo.png", alt: "The New York Times", size: "lg" },
  { src: "/images/people-logo.png", alt: "People", size: "sm" },
  { src: "/images/ny-post-logo-transparent.png", alt: "New York Post", size: "sm" },
  { src: "/images/home-press-gothamist.png", alt: "Gothamist", size: "lg" },
  { src: "/images/home-press-daily-mail.png", alt: "Daily Mail", size: "xl" },
  { src: "/images/home-press-eater.png", alt: "Eater", size: "xl" },
];

/** Logos shipped with off-white rectangular backgrounds in the asset file */
const LOGOS_WITH_LIGHT_BACKGROUND = new Set([
  "The New York Times",
  "The New Yorker",
  "Gothamist",
  "Daily Mail",
  "Eater",
]);

const sizeStyles: Record<
  LogoSize,
  { height: { xs: number; md: number }; maxWidth: { xs: number; md: number } }
> = {
  sm: {
    height: { xs: 32, md: 40 },
    maxWidth: { xs: 100, md: 120 },
  },
  lg: {
    height: { xs: 56, md: 80 },
    maxWidth: { xs: 220, md: 280 },
  },
  xl: {
    height: { xs: 52, md: 84 },
    maxWidth: { xs: 200, md: 300 },
  },
};

const SCALE_ALL = 1.1;
const SCALE_DAILY_MAIL = 1.25 * 1.15;
const SCALE_EATER = SCALE_ALL * 0.92 * 0.96;

function scaledStyles(
  size: LogoSize,
  options: { dailyMail: boolean; eater: boolean },
): {
  height: { xs: number; md: number };
  maxWidth: { xs: number; md: number };
} {
  const s = sizeStyles[size];
  const mult = options.dailyMail
    ? SCALE_DAILY_MAIL
    : options.eater
      ? SCALE_EATER
      : SCALE_ALL;
  return {
    height: {
      xs: Math.round(s.height.xs * mult),
      md: Math.round(s.height.md * mult),
    },
    maxWidth: {
      xs: Math.round(s.maxWidth.xs * mult),
      md: Math.round(s.maxWidth.md * mult),
    },
  };
}

const marqueeKeyframes = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-25%); }
`;

export default function PressLogosRow() {
  const [paused, setPaused] = useState(false);
  const duplicated = [...pressLogos, ...pressLogos, ...pressLogos, ...pressLogos];

  return (
    <ScrollReveal variant="fadeIn" duration={0.8}>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#ffffff",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          py: { xs: 3, md: 4 },
          px: { xs: 0, md: 2 },
        }}
      >
        <Box
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          sx={{
            overflow: "hidden",
            width: "100%",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 4, md: 6 },
              width: "max-content",
              backgroundColor: "#ffffff",
              animation: `${marqueeKeyframes} 45s linear infinite`,
              animationPlayState: paused ? "paused" : "running",
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {duplicated.map((logo, i) => {
              const s = scaledStyles(logo.size, {
                dailyMail: logo.alt === "Daily Mail",
                eater: logo.alt === "Eater",
              });
              const lightBg = LOGOS_WITH_LIGHT_BACKGROUND.has(logo.alt);

              return (
                <Box
                  key={`${logo.alt}-${i}`}
                  sx={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    px: lightBg ? { xs: 1, md: 1.5 } : 0,
                    py: lightBg ? { xs: 0.75, md: 1 } : 0,
                    lineHeight: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={logo.src}
                    alt={logo.alt}
                    sx={{
                      height: s.height,
                      width: "auto",
                      maxWidth: s.maxWidth,
                      objectFit: "contain",
                      opacity: 0.9,
                      backgroundColor: "#ffffff",
                      ...(lightBg && { mixBlendMode: "multiply" }),
                      transition: "opacity 0.2s ease",
                      "&:hover": { opacity: 1 },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </ScrollReveal>
  );
}
